import { Component } from '@theme/component'

class CollectionSlider extends Component {
  requiredRefs = ['scroller']

  _cachedDimensions = {
    clientWidth: 0,
    scrollerWidth: 0
  }

  currentIndex = 0
  _snapDelay = 140
  _isUserScrolling = false
  _contentObserver = null
  _resizeObserver = null
  _resizeRAF = null
  _scrollEndTimer = null
  _scrollRAF = null

  connectedCallback() {
    super.connectedCallback()

    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    scroller.addEventListener('scroll', this._handleScroll, { passive: true })
    window.addEventListener('resize', this._handleResize, { passive: true })

    this._observeSliderSizing()
    this._refreshSliderState()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    const { scroller } = this.refs
    if (scroller) {
      scroller.removeEventListener('scroll', this._handleScroll)
    }
    window.removeEventListener('resize', this._handleResize)
    this._disconnectSliderSizing()
    this._clearPendingWork()
  }

  next(event) {
    event?.preventDefault?.()
    this._cacheDimensions()
    const step = this._getScrollStep()
    if (step === 'page') {
      const { scroller } = this.refs
      if (!scroller) {
        return
      }

      const scrollAmount = this._getScrollAmount()
      this._scrollBy(scrollAmount)
      return
    }

    this._selectByOffset(1)
  }

  prev(event) {
    event?.preventDefault?.()
    this._cacheDimensions()
    const step = this._getScrollStep()
    if (step === 'page') {
      const { scroller } = this.refs
      if (!scroller) {
        return
      }

      const scrollAmount = this._getScrollAmount()
      this._scrollBy(-scrollAmount)
      return
    }

    this._selectByOffset(-1)
  }

  select(index, event) {
    event?.preventDefault?.()
    this._cacheDimensions()
    const cards = this._getCards()
    if (!cards.length) {
      return
    }

    const nextIndex = Math.max(0, Math.min(index, cards.length - 1))
    const target = cards[nextIndex]
    if (!target) {
      return
    }

    this._scrollTo(this._clampScrollLeft(target.offsetLeft))
    this._setCurrentIndex(nextIndex)
  }

  _selectByOffset(offset) {
    const cards = this._getCards()
    if (!cards.length) {
      return
    }

    const currentIndex = this._getCurrentIndex()
    const fallbackIndex = Math.max(
      0,
      Math.min(this.currentIndex, cards.length - 1)
    )
    const selectedIndex = currentIndex ?? fallbackIndex

    this.select(selectedIndex + offset)
  }

  _observeSliderSizing() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    this._resizeObserver = new ResizeObserver(this._queueSliderStateUpdate)
    this._resizeObserver.observe(scroller)

    const track = this._getTrackElement()
    if (track) {
      this._resizeObserver.observe(track)
    }

    this._contentObserver = new MutationObserver(this._queueSliderStateUpdate)
    this._contentObserver.observe(scroller, { childList: true, subtree: true })
  }

  _disconnectSliderSizing() {
    this._contentObserver?.disconnect()
    this._contentObserver = null
    this._resizeObserver?.disconnect()
    this._resizeObserver = null
  }

  _queueSliderStateUpdate = () => {
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF)
    }

    this._resizeRAF = requestAnimationFrame(() => {
      this._refreshSliderState()
      this._resizeRAF = null
    })
  }

  _refreshSliderState() {
    this._cacheDimensions()
    this._syncScrollPosition()
    this._updateButtonStates()
  }

  _cacheDimensions() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    const { scrollWidth, clientWidth } = scroller

    this._cachedDimensions = {
      clientWidth,
      scrollerWidth: clientWidth,
      scrollWidth
    }
  }

  _getScrollAmount() {
    return this._cachedDimensions.scrollerWidth || 0
  }

  _getMaxScroll() {
    const { scrollWidth = 0, clientWidth = 0 } = this._cachedDimensions
    return Math.max(0, scrollWidth - clientWidth)
  }

  _clampScrollLeft(position) {
    const maxScroll = this._getMaxScroll()
    return Math.max(0, Math.min(position, maxScroll))
  }

  _getScrollStep() {
    return this.getAttribute('scroll-step') || 'item'
  }

  _getCards() {
    const { scroller } = this.refs
    if (!scroller) {
      return []
    }

    const selector = this.getAttribute('selector') || '.card'
    return Array.from(scroller.querySelectorAll(selector))
  }

  _getDots() {
    if (!this.id) {
      return []
    }
    return Array.from(
      document.querySelectorAll(`.dots__dot[aria-controls="${this.id}"]`)
    )
  }

  _getCurrentIndex() {
    const closestCard = this._getClosestCard()
    if (!closestCard) {
      return null
    }

    return closestCard.index
  }

  _getClosestCard() {
    const { scroller } = this.refs
    const cards = this._getCards()
    if (!(scroller && cards.length)) {
      return null
    }

    const scrollLeft = scroller.scrollLeft
    let index = 0
    let minDistance = Number.POSITIVE_INFINITY

    for (const [cardIndex, card] of cards.entries()) {
      const targetLeft = this._clampScrollLeft(card.offsetLeft)
      const distance = Math.abs(targetLeft - scrollLeft)
      if (distance >= minDistance) {
        continue
      }
      minDistance = distance
      index = cardIndex
    }

    return { index, minDistance }
  }

  _getTrackElement() {
    const { scroller } = this.refs
    const track = scroller?.firstElementChild
    return track instanceof HTMLElement ? track : null
  }

  _scrollBy(amount) {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    scroller.scrollBy({
      behavior: 'smooth',
      left: amount
    })
  }

  _scrollTo(position) {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    scroller.scrollTo({
      behavior: 'smooth',
      left: this._clampScrollLeft(position)
    })
  }

  _handleScroll = () => {
    if (this._scrollRAF) {
      cancelAnimationFrame(this._scrollRAF)
    }

    if (this._scrollEndTimer) {
      clearTimeout(this._scrollEndTimer)
    }
    this._scrollEndTimer = setTimeout(() => {
      this._scrollEndTimer = null
      this._isUserScrolling = false
      this._snapToClosest()
    }, this._snapDelay)
    this._isUserScrolling = true

    this._scrollRAF = requestAnimationFrame(() => {
      this._updateButtonStates()
      this._scrollRAF = null
    })
  }

  _handleResize = () => {
    this._queueSliderStateUpdate()
  }

  _syncScrollPosition() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    const nextScrollLeft = this._clampScrollLeft(scroller.scrollLeft)
    if (nextScrollLeft === scroller.scrollLeft) {
      return
    }

    scroller.scrollLeft = nextScrollLeft
  }

  _updateButtonStates() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    this._syncCurrentIndex()
    const sliderState = this._getSliderState(scroller)
    this._dispatchSliderState(sliderState)
    this._syncEdgeAttributes(sliderState)
  }

  _syncCurrentIndex() {
    if (this._isUserScrolling) {
      return
    }

    const currentIndex = this._getCurrentIndex()
    this._setCurrentIndex(currentIndex)
  }

  _getSliderState(scroller) {
    const scrollLeft = scroller.scrollLeft
    const { scrollWidth, clientWidth } = this._cachedDimensions

    return {
      atEnd: scrollLeft + clientWidth >= scrollWidth - 1,
      atStart: scrollLeft <= 1
    }
  }

  _dispatchSliderState(sliderState) {
    this.dispatchEvent(
      new CustomEvent('slider-state-change', {
        bubbles: true,
        detail: sliderState
      })
    )
  }

  _syncEdgeAttributes({ atEnd, atStart }) {
    this.toggleAttribute('at-start', atStart)
    this.toggleAttribute('at-end', atEnd)
  }

  _snapToClosest() {
    const cards = this._getCards()
    const closestCard = this._getClosestCard()

    if (!closestCard) {
      return
    }

    if (closestCard.minDistance <= 4) {
      this._setCurrentIndex(closestCard.index)
      return
    }

    const target = cards[closestCard.index]
    if (!target) {
      return
    }

    this._scrollTo(this._clampScrollLeft(target.offsetLeft))
    this._setCurrentIndex(closestCard.index)
  }

  _setCurrentIndex(index) {
    if (index === null || Number.isNaN(index)) {
      return
    }
    if (this.currentIndex === index) {
      return
    }
    this.currentIndex = index
    this._updateDots()
  }

  _updateDots() {
    const dots = this._getDots()
    if (!dots.length) {
      return
    }
    for (const dot of dots) {
      const dotIndex = Number.parseInt(dot.dataset.index, 10)
      if (Number.isNaN(dotIndex)) {
        continue
      }
      dot.setAttribute(
        'aria-current',
        dotIndex === this.currentIndex ? 'true' : 'false'
      )
    }
  }

  _clearPendingWork() {
    if (this._scrollEndTimer) {
      clearTimeout(this._scrollEndTimer)
      this._scrollEndTimer = null
    }
    if (this._scrollRAF) {
      cancelAnimationFrame(this._scrollRAF)
      this._scrollRAF = null
    }
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF)
      this._resizeRAF = null
    }
  }
}

if (!customElements.get('just-slider')) {
  customElements.define('just-slider', CollectionSlider)
}

export { CollectionSlider }
